import { ReactNode } from "react";
import { Box, Group, Stack, Text } from "@mantine/core";

export interface BarListItem {
    label: ReactNode;
    value: number;
    // Optional per-item fill colour; falls back to the component-level `color`.
    color?: string;
}

export interface BarListProps {
    items: BarListItem[];
    // Bars are scaled against this; defaults to the largest value in the set.
    max?: number;
    color?: string;
    labelWidth?: number;
}

// A horizontal bar list: fixed-width label, a full-width light track with a
// proportional coloured fill, and the raw value on the right. Used for both the
// star-rating distribution and the questionnaire answer counts so the Report
// speaks one visual language (no vendor chart with its own axis lines / stamps).
export const BarList = ({ items, max, color = "#FB5B14", labelWidth = 120 }: BarListProps) => {
    const resolvedMax = max ?? Math.max(...items.map((item) => item.value), 1);

    return (
        <Stack spacing={14}>
            {items.map((item, index) => (
                <Group key={index} spacing={12} noWrap align="center">
                    <Box style={{ width: labelWidth, flex: "none" }}>
                        {/* No lineClamp: answer/label text must always show in full (wraps to as
                            many lines as needed) rather than being truncated with an ellipsis. */}
                        <Text size="sm" color="#39435A">
                            {item.label}
                        </Text>
                    </Box>
                    <Box style={{ flex: 1, height: 12, background: "#EEF1F6", borderRadius: 999 }}>
                        <Box
                            style={{
                                width: item.value > 0 ? `${Math.max((item.value / resolvedMax) * 100, 4)}%` : 0,
                                height: 12,
                                background: item.color ?? color,
                                borderRadius: 999,
                            }}
                        />
                    </Box>
                    <Text
                        weight={700}
                        color="#0F1B3D"
                        style={{ minWidth: 28, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                    >
                        {item.value}
                    </Text>
                </Group>
            ))}
        </Stack>
    );
};
